import React, { useState } from 'react';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { cn } from '../../lib/utils';
import { formatDate } from '../../config/appConfig';
import { Badge, Button, Card, Label, Select, type SelectOption, Textarea, DateInput, DateTimeInput, ConfirmationDialog, PlusIcon, PencilIcon, TrashIcon, CheckIcon, XIcon } from '../../components/ui';

const API = '/support-agreement-renewal-reminders';

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  renewed: '#10b981',
  terminating: '#ef4444',
  no_response: '#64748b',
  will_proceed: '#3b82f6',
};
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  renewed: 'Renewed',
  terminating: 'Terminating',
  no_response: 'No response',
  will_proceed: 'Will proceed',
};

/** Exported for use in list column (config). */
export const renderAgreementRenewalStatusBadge = (status?: string | null) => {
  const key = (status || 'pending').toLowerCase();
  const label = STATUS_LABELS[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—');
  const hex = STATUS_COLORS[key] ?? STATUS_COLORS.no_response;
  return <Badge color={hex}>{label}</Badge>;
};

const renderStatusBadge = renderAgreementRenewalStatusBadge;

export interface SupportAgreementRenewalReminder {
  id: number;
  supportAgreementId: number;
  contactedAt: string;
  channel: string | null;
  responseStatus: string | null;
  responseNote: string | null;
  responseAt: string | null;
  createdById: number | null;
  updatedById: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy?: { userFirstName?: string; userLastName?: string };
  updatedBy?: { userFirstName?: string; userLastName?: string };
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: '—' },
  { value: 'pending', label: 'Pending' },
  { value: 'renewed', label: 'Renewed' },
  { value: 'terminating', label: 'Terminating' },
  { value: 'will_proceed', label: 'Will proceed' },
  { value: 'no_response', label: 'No response' },
];
const CHANNEL_OPTIONS: SelectOption[] = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' },
];

interface SupportAgreementRenewalRemindersSectionProps {
  supportAgreementId: number;
  onReminderAdded?: () => void;
  variant?: 'inline' | 'panel';
}

export const SupportAgreementRenewalRemindersSection: React.FC<SupportAgreementRenewalRemindersSectionProps> = ({
  supportAgreementId,
  onReminderAdded,
  variant = 'inline',
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { data: reminders = [], mutate } = useSWR<SupportAgreementRenewalReminder[]>(
    `${API}?supportAgreementId=${supportAgreementId}`,
    fetcher
  );
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [form, setForm] = useState({ contactedAt: new Date().toISOString(), channel: 'email' });
  const [editForm, setEditForm] = useState({ responseStatus: '', responseNote: '', responseAt: '' });

  const handleAdd = async () => {
    try {
      await api.post(API, {
        supportAgreementId,
        contactedAt: new Date(form.contactedAt).toISOString(),
        channel: form.channel || undefined,
        responseStatus: 'pending',
      });
      showSuccess('Reminder logged.');
      setAdding(false);
      setForm({ contactedAt: new Date().toISOString(), channel: 'email' });
      mutate();
      onReminderAdded?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; message?: string }; message?: string };
      showError(err.response?.data?.message || err.message || 'Failed to add reminder');
    }
  };

  const openEdit = (r: SupportAgreementRenewalReminder) => {
    setEditingId(r.id);
    setEditForm({
      responseStatus: r.responseStatus || '',
      responseNote: r.responseNote || '',
      responseAt: r.responseAt ? r.responseAt.slice(0, 10) : '',
    });
  };

  const handleUpdate = async () => {
    if (editingId == null) return;
    try {
      await api.patch(`${API}/${editingId}`, {
        responseStatus: editForm.responseStatus || undefined,
        responseNote: editForm.responseNote || undefined,
        responseAt: editForm.responseAt ? new Date(editForm.responseAt).toISOString() : undefined,
      });
      showSuccess('Response updated.');
      setEditingId(null);
      mutate();
      onReminderAdded?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; message?: string }; message?: string };
      showError(err.response?.data?.message || err.message || 'Failed to update');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId == null) return;
    try {
      await api.delete(`${API}/${deleteConfirmId}`);
      showSuccess('Reminder deleted.');
      setDeleteConfirmId(null);
      mutate();
      onReminderAdded?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; message?: string }; message?: string };
      showError(err.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  const formBlock = (
    <Card variant="outlined" className={cn('p-4', variant === 'panel' && 'mb-6')}>
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-wide mb-1">Contacted at</Label>
          <DateTimeInput value={form.contactedAt} onChange={(e) => setForm((f) => ({ ...f, contactedAt: e.target.value }))} showClearButton={false} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide mb-1">Channel</Label>
          <Select value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))} options={CHANNEL_OPTIONS} placeholder="Channel" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" onClick={handleAdd} variant="default">
          <PlusIcon className="w-4 h-4" /> Save reminder
        </Button>
        <Button type="button" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
      </div>
    </Card>
  );

  if (variant === 'panel') {
    return (
      <div className="space-y-6">
        <section className="space-y-3">
          <h3 className={cn('text-sm font-semibold uppercase tracking-wide', colors.text)}>Log reminder</h3>
          {adding ? formBlock : (
            <Button type="button" variant="outline" onClick={() => setAdding(true)} className="w-full py-4 border-2 border-dashed">
              <PlusIcon className="w-5 h-5" /> Log reminder
            </Button>
          )}
        </section>
        <section className="space-y-3">
          <h3 className={cn('text-sm font-semibold uppercase tracking-wide', colors.text)}>Reminders ({reminders.length})</h3>
          {reminders.length === 0 && !adding ? (
            <Card variant="outlined" className={cn('rounded-xl border-dashed p-6 text-center text-sm', colors.textMuted)}>
              <p>No reminders yet. Log one when you contact the client about renewal.</p>
            </Card>
          ) : (
            <ul className="space-y-3">
              {reminders.map((r) => (
                <li key={r.id}>
                  <Card variant="default" className="p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className={cn('text-sm font-medium', colors.text)}>{formatDate(r.contactedAt)}</span>
                        {r.channel && <Badge variant="secondary">{String(r.channel)}</Badge>}
                        {r.responseStatus && renderStatusBadge(r.responseStatus)}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {editingId === r.id ? (
                          <>
                            <button type="button" onClick={handleUpdate} className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg" title="Save" aria-label="Save">
                              <CheckIcon className="w-5 h-5" />
                            </button>
                            <button type="button" onClick={() => setEditingId(null)} className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Cancel" aria-label="Cancel">
                              <XIcon className="w-5 h-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => openEdit(r)} className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg" title="Edit response" aria-label="Edit response">
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button type="button" onClick={() => setDeleteConfirmId(r.id)} className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 rounded-lg" title="Delete" aria-label="Delete">
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {editingId === r.id ? (
                      <div className={cn('space-y-3 pt-2 border-t', colors.border)}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs mb-1">Response date</Label>
                            <DateInput value={editForm.responseAt} onChange={(e) => setEditForm((f) => ({ ...f, responseAt: e.target.value }))} showClearButton={false} />
                          </div>
                          <div>
                            <Label className="text-xs mb-1">Response status</Label>
                            <Select value={editForm.responseStatus} onChange={(e) => setEditForm((f) => ({ ...f, responseStatus: e.target.value }))} options={STATUS_OPTIONS} placeholder="Status" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs mb-1">Response note</Label>
                          <Textarea value={editForm.responseNote} onChange={(e) => setEditForm((f) => ({ ...f, responseNote: e.target.value }))} rows={2} placeholder="Optional note" className="min-h-0" />
                        </div>
                      </div>
                    ) : (
                      <>
                        {r.responseAt && <p className={cn('text-xs mb-1', colors.textMuted)}>Response: {formatDate(r.responseAt)}</p>}
                        {r.responseNote && <p className={cn('text-sm', colors.text)}>{r.responseNote}</p>}
                      </>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
        <ConfirmationDialog
          isOpen={deleteConfirmId != null}
          onClose={() => setDeleteConfirmId(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete reminder"
          message="Are you sure you want to delete this reminder?"
          confirmText="Delete"
          confirmVariant="danger"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {adding && formBlock}
      <ul className="space-y-2">
        {reminders.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
            <span>{formatDate(r.contactedAt)}</span>
            {r.responseStatus && renderStatusBadge(r.responseStatus)}
          </li>
        ))}
      </ul>
      <ConfirmationDialog isOpen={deleteConfirmId != null} onClose={() => setDeleteConfirmId(null)} onConfirm={handleDeleteConfirm} title="Delete reminder" message="Are you sure?" confirmText="Delete" confirmVariant="danger" />
    </div>
  );
};
